import type { CellContext, ColumnDef } from "@tanstack/react-table";

import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import DesoAvatar from "@/components/shared/deso-avatar";
import { MaybeUserLabel } from "@/components/shared/maybe-user-label";
import type { TradingTopTradersByTokenByVolume } from "@/graphql/codegen/graphql";
import { FOCUS_TOKEN_DISPLAY_NAME } from "@/utils/constants";
import getContentBlurredStyle from "@/utils/get-content-blurred-style";

import { renderSortingHeader } from "../table-sorting-headers";
import TopTradersActions from "./coin-actions";

interface CustomCellContext extends CellContext<
  TradingTopTradersByTokenByVolume,
  unknown
> {
  focusExchangeRate: number;
  pageOffset: number;
  currentUserPublicKey: string;
  refetchData: () => void;
}

export const columns = (): ColumnDef<TradingTopTradersByTokenByVolume>[] => {
  return [
    {
      accessorKey: "traderPublicKey",
      header: () => <span className="text-xxs">Trader</span>,
      cell: ({ row }) => {
        if (!row.original.traderPublicKey) {
          return (
            <div className="flex items-center gap-2">
              <DesoAvatar account={null} size={32} />{" "}
              <span
                className="
                  truncate font-sans text-sm text-muted underline-offset-4
                "
              >
                Unknown
              </span>
            </div>
          );
        }
        return (
          <MaybeUserLabel
            value={row.original.traderPublicKey}
            showDisplayName={true}
            shortenIfNoProfile={true}
            showAvatarIfNoProfile={true}
            clickable={true}
            imageSize={32}
            className="items-center"
            displayNameWrapperClassName="w-[230px]"
            displayNameClassName="pl-0 w-[230px] text-sm text-left grow overflow-hidden underline-offset-4 truncate font-sans"
            style={getContentBlurredStyle(row.original.traderPublicKey?.length)}
          />
        );
      },
    },
    {
      accessorKey: "totalVolumeUsd",
      header: ({ column }) =>
        renderSortingHeader({
          column,
          title: "Trade Volume",
          className: "text-xxs",
        }),
      cell: (props) => {
        const { row } = props as CustomCellContext;
        const totalVolumeUsd = row.original.totalVolumeUsd || 0;
        const totalVolumeFocus = row.original.totalVolumeFocus || 0;
        return (
          <div
            className="
            flex flex-col gap-0 text-left font-mono text-foreground
          "
          >
            <CoinPriceFormatter
              price={totalVolumeUsd || 0}
              className="text-xs"
            />
            <div
              className="
                flex items-center gap-1 text-[10px] text-muted
                hover:text-foreground
              "
            >
              <CoinPriceFormatter
                price={totalVolumeFocus}
                highPriceThreshold={10}
                isUsd={false}
                className="text-[10px]"
              />{" "}
              <span>{FOCUS_TOKEN_DISPLAY_NAME}</span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "traderTokenBalance",
      header: () => <span className="text-xxs">Portfolio Value</span>,
      cell: (props) => {
        const { row } = props as CustomCellContext;
        const traderTotalBalanceUsd = row.original.traderTotalBalanceUsd || 0;
        const traderTotalBalanceFocus =
          row.original.traderTotalBalanceFocus || 0;
        return (
          <div
            className="
            flex flex-col gap-0 text-left font-mono text-foreground
          "
          >
            <CoinPriceFormatter
              price={traderTotalBalanceUsd}
              className="text-xs"
            />
            <div
              className="
                flex items-center gap-1 text-[10px] text-muted
                hover:text-foreground
              "
            >
              <CoinPriceFormatter
                price={traderTotalBalanceFocus}
                highPriceThreshold={10}
                isUsd={false}
                className="text-[10px]"
              />{" "}
              <span>{FOCUS_TOKEN_DISPLAY_NAME}</span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "boughtOrSold",
      header: () => <span className="text-xxs">Bought/Sold</span>,
      cell: () => {
        return <div className="text-xs text-muted">Coming Soon!</div>;
      },
    },
    {
      accessorKey: "realizedPnL",
      header: () => <span className="text-xxs">Realized PnL</span>,
      cell: () => {
        return <div className="text-xs text-muted">Coming Soon!</div>;
      },
    },
    {
      accessorKey: "unrealizedPnL",
      header: () => <span className="text-xxs">Unrealized PnL</span>,
      cell: () => {
        return <div className="text-xs text-muted">Coming Soon!</div>;
      },
    },
    {
      id: "actions",
      cell: (props) => {
        const { row } = props as CustomCellContext;
        return (
          <TopTradersActions
            username={row.original.traderUsername ?? ""}
            publicKey={row.original.traderPublicKey ?? ""}
          />
        );
      },
    },
  ];
};
